import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  FormGroup,
} from '@mui/material';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import {
  INTENSITY_LEVEL_VALUES,
  PERFORMANCE_CONTEXT_VALUES,
  type IntensityLevel,
  type PerformanceContext,
} from '../../contracts/openapi/assetContracts';
import {
  intensityLevelLabels,
  performanceContextLabels,
} from '../../contracts/openapi/assetEnumLabels';
import { environmentSafeLog } from '../../utils/environment';
import { MovesLayerMetadata } from '../../types/referencelayerMetadata';
import { API_STRING_MAX_LENGTH, MOVES_METADATA } from '../../constants/apiMetadataConstraints';
import {
  buildEnumOptions,
  coerceEnumArray,
  coerceEnumValue,
} from './enumFieldUtils';

interface MovesMetadataEditorProps {
  layerMetadata: MovesLayerMetadata;
  isEditable?: boolean;
  onMetadataChange?: (updatedMetadata: MovesLayerMetadata) => void;
  // Legacy support during migration
  asset?: any;
  // AI-generated description for fallback pattern (Issue #15)
  aiGeneratedDescription?: string;
}

/**
 * Apply intelligent defaults for Moves layer to prevent null/undefined/empty values
 * This ensures all fields have sensible defaults to avoid backend validation errors
 * Similar to Stars layer implementation - provides smart fallbacks
 * when AI doesn't detect values or returns null/empty fields
 */
export const applyIntelligentMovesDefaults = (data: any) => {
  // Start with existing data, then apply defaults for any null/undefined values
  const defaults = {
    // Core move information - mandatory fields must have non-empty defaults
    moveName: 'Unknown Move',
    choreographerName: '',
    danceStyle: 'Unknown', // Mandatory field - must have default
    substyle: '', // Let AI detect

    // Movement characteristics with intelligent defaults - mandatory fields must have non-empty defaults
    energy: 'medium',
    complexity: 'intermediate',
    tempo: 'medium', // Mandatory field
    difficulty: 'intermediate', // Mandatory field
    duration: 0,
    bpm: 0,

    // Performance characteristics
    movementPatterns: [],
    energyTempo: '',
    danceClassification: '',
    culturalContext: '',
    culturalOrigin: '',

    // Musical compatibility
    musicGenre: '',
    bodyParts: [],

    // V2.0 compatibility
    layerType: 'moves',
  };

  // Apply defaults only for null, undefined values (NOT empty strings)
  const result = { ...data };

  // Apply defaults for null/undefined fields
  Object.keys(defaults).forEach(key => {
    if (result[key] === null || result[key] === undefined) {
      const defaultValue = defaults[key as keyof typeof defaults];
      if (Array.isArray(defaultValue)) {
        result[key] = [];
      } else {
        result[key] = defaultValue;
      }
    }
  });

  // Ensure arrays are arrays (not null/undefined)
  if (!Array.isArray(result.movementPatterns)) {
    result.movementPatterns = [];
  }
  if (!Array.isArray(result.bodyParts)) {
    result.bodyParts = [];
  }

  return result;
};

const MovesMetadataEditor: React.FC<MovesMetadataEditorProps> = ({
  layerMetadata,
  asset,
  isEditable = false,
  onMetadataChange,
  aiGeneratedDescription,
}) => {
  // V2.0 ARCHITECTURE: Use layerMetadata first, with legacy fallback
  const moveMetadata = React.useMemo(() => {
    // Priority 1: V2.0 layerMetadata prop (preferred)
    if (layerMetadata) {
      environmentSafeLog('📌 [MOVES V2.0] Using layerMetadata prop');
      // Apply intelligent defaults even when layerMetadata is provided
      const processedMetadata = applyIntelligentMovesDefaults(layerMetadata);
      return processedMetadata;
    }

    // Legacy fallback for backward compatibility
    console.log('🚨 [MOVES AI CONTRACT] Full asset object received:', asset);

    environmentSafeLog(
      '🔎 [MOVES AI CONTRACT] Extracting movesMetadata from asset:',
      {
        hasMovesMetadata: !!asset?.movesMetadata,
        hasAiContractMetadata: !!asset?.aiMetadata?.layerMetadata,
        hasAiMovesMetadata: !!asset?.aiMetadata?.movesMetadata,
        hasMetadataMovesMetadata: !!asset?.metadata?.movesMetadata,
      }
    );

    let rawMetadata = null;

    // Priority 1: AI Contract layerMetadata (from AI Response Contract)
    if (asset?.aiMetadata?.layerMetadata && asset.layer === 'M') {
      environmentSafeLog(
        '📌 [MOVES AI CONTRACT] Using aiMetadata.layerMetadata from AI Response Contract'
      );
      rawMetadata = asset.aiMetadata.layerMetadata;
    }
    // Priority 2: Root level movesMetadata (legacy AI-generated)
    else if (asset.movesMetadata) {
      environmentSafeLog(
        '📌 [MOVES AI CONTRACT] Using root movesMetadata (legacy)'
      );
      rawMetadata = asset.movesMetadata;
    }
    // Priority 3: AI metadata structure (nested legacy)
    else if (asset.aiMetadata?.movesMetadata) {
      environmentSafeLog(
        '📌 [MOVES AI CONTRACT] Using aiMetadata.movesMetadata (legacy)'
      );
      rawMetadata = asset.aiMetadata.movesMetadata;
    }
    // Priority 4: Legacy metadata structure
    else if (asset.metadata?.movesMetadata) {
      environmentSafeLog(
        '📌 [MOVES AI CONTRACT] Using metadata.movesMetadata (legacy)'
      );
      rawMetadata = asset.metadata.movesMetadata;
    } else {
      environmentSafeLog(
        '⚠️ [MOVES AI CONTRACT] No move metadata found, using empty object'
      );
      rawMetadata = {};
    }

    // Apply nested data access pattern for backward compatibility
    const actualMoveMetadata =
      rawMetadata?.movesMetadata ||
      rawMetadata?.data?.movesMetadata ||
      rawMetadata;
    environmentSafeLog(
      '🔄 [MOVES AI CONTRACT] Final metadata after nested access:',
      actualMoveMetadata
    );

    // INTELLIGENT DEFAULTS: Apply smart defaults to handle null/missing values
    const processedMetadata = applyIntelligentMovesDefaults(actualMoveMetadata);

    environmentSafeLog(
      '💃 [MOVES INTELLIGENT DEFAULTS] Applied smart defaults to Moves metadata',
      { original: actualMoveMetadata, processed: processedMetadata }
    );

    return processedMetadata;
  }, [layerMetadata, asset]);

  // State for editable fields - initialize with extracted metadata
  const [editableData, setEditableData] = React.useState(() => {
    console.log(
      '🚀 [MOVES INFO INIT] Initializing editableData state with moveMetadata:',
      moveMetadata
    );

    // CRITICAL FIX (Issue #15): Auto-populate description with AI-generated description
    // Pattern: AI description → creator description → empty string
    const initialDescription =
      aiGeneratedDescription ||
      moveMetadata?.creatorDescription ||
      moveMetadata?.description ||
      '';
    if (aiGeneratedDescription && !moveMetadata?.creatorDescription && !moveMetadata?.description) {
      console.log('📝 [MOVES INFO] Auto-populating Move Description with AI-generated description:', aiGeneratedDescription);
    }

    return {
      // Creator Input (CRITICAL for AI)
      creatorDescription: initialDescription,

      // Mandatory fields (tempo can be number from extract-metadata/move bpm)
      moveName: moveMetadata?.moveName || '',
      tempo: typeof moveMetadata?.tempo === 'number' ? String(moveMetadata.tempo) : (typeof moveMetadata?.tempo === 'string' ? moveMetadata.tempo : ''),
      danceStyle: moveMetadata?.danceStyle || '',
      difficulty: typeof moveMetadata?.difficulty === 'number' ? String(moveMetadata.difficulty) : (typeof moveMetadata?.difficulty === 'string' ? moveMetadata.difficulty : ''),

      // Optional fields
      substyle: moveMetadata?.substyle || '',
      choreographerName: moveMetadata?.choreographerName || '',
      complexity: moveMetadata?.complexity || '',
      skill_level_required: moveMetadata?.skill_level_required || '',
      energy: coerceEnumValue(moveMetadata?.energy, INTENSITY_LEVEL_VALUES),
      intensity: moveMetadata?.intensity || '',
      pace: moveMetadata?.pace || '',
      bpmRange: moveMetadata?.bpmRange || { min: 0, max: 0 },
      movementStyle: moveMetadata?.movementStyle || '',
      bodyParts: Array.isArray(moveMetadata?.bodyParts)
        ? moveMetadata?.bodyParts
        : typeof moveMetadata?.bodyParts === 'string' && moveMetadata?.bodyParts
        ? moveMetadata.bodyParts.split(',').map((p: string) => p.trim())
        : [],
      spaceRequired: moveMetadata?.spaceRequired || '',
      floorWork: moveMetadata?.floorWork || false,
      partnered: moveMetadata?.partnered || false,
      groupSize: moveMetadata?.groupSize || '',
      signature_moves: Array.isArray(moveMetadata?.signature_moves)
        ? moveMetadata?.signature_moves
        : typeof moveMetadata?.signature_moves === 'string' &&
          moveMetadata?.signature_moves
        ? moveMetadata.signature_moves.split(',').map((m: string) => m.trim())
        : [],
      techniques_required: Array.isArray(moveMetadata?.techniques_required)
        ? moveMetadata?.techniques_required
        : typeof moveMetadata?.techniques_required === 'string' &&
          moveMetadata?.techniques_required
        ? moveMetadata.techniques_required
            .split(',')
            .map((t: string) => t.trim())
        : [],
      props_needed: Array.isArray(moveMetadata?.props_needed)
        ? moveMetadata?.props_needed
        : typeof moveMetadata?.props_needed === 'string' &&
          moveMetadata?.props_needed
        ? moveMetadata.props_needed.split(',').map((p: string) => p.trim())
        : [],
      costume_requirements: moveMetadata?.costume_requirements || '',
      performanceContext: coerceEnumArray(
        moveMetadata?.performanceContext ?? moveMetadata?.performance_context,
        PERFORMANCE_CONTEXT_VALUES,
        10
      ),
      audience_type: moveMetadata?.audience_type || '',
    };
  });

  // Track metadata hash to prevent infinite update loops
  const metadataHashRef = useRef<string>('');
  const computedHash = JSON.stringify(moveMetadata);

  // 🔧 FIX: Use ref to track latest editableData for cleanup without causing re-renders
  const editableDataRef = useRef(editableData);
  useEffect(() => {
    editableDataRef.current = editableData;
  }, [editableData]);

  // Update editableData when moveMetadata changes (from AI generation)
  React.useEffect(() => {
    if (computedHash !== metadataHashRef.current) {
      console.log(
        '🔄 [MOVES INFO] moveMetadata changed, updating editableData'
      );
      metadataHashRef.current = computedHash;

      setEditableData({
        // Creator Input (CRITICAL for AI)
        creatorDescription: moveMetadata?.creatorDescription || '',

        // Mandatory fields (tempo/difficulty can be number from extract-metadata/move)
        moveName: moveMetadata?.moveName || '',
        tempo: typeof moveMetadata?.tempo === 'number' ? String(moveMetadata.tempo) : (typeof moveMetadata?.tempo === 'string' ? moveMetadata.tempo : ''),
        danceStyle: moveMetadata?.danceStyle || '',
        difficulty: typeof moveMetadata?.difficulty === 'number' ? String(moveMetadata.difficulty) : (typeof moveMetadata?.difficulty === 'string' ? moveMetadata.difficulty : ''),

        // Optional fields
        substyle: moveMetadata?.substyle || '',
        choreographerName: moveMetadata?.choreographerName || '',
        complexity: moveMetadata?.complexity || '',
        skill_level_required: moveMetadata?.skill_level_required || '',
        energy: coerceEnumValue(moveMetadata?.energy, INTENSITY_LEVEL_VALUES),
        intensity: moveMetadata?.intensity || '',
        pace: moveMetadata?.pace || '',
        bpmRange: moveMetadata?.bpmRange || { min: 0, max: 0 },
        movementStyle: moveMetadata?.movementStyle || '',
        bodyParts: Array.isArray(moveMetadata?.bodyParts)
          ? moveMetadata?.bodyParts
          : typeof moveMetadata?.bodyParts === 'string' &&
            moveMetadata?.bodyParts
          ? moveMetadata.bodyParts.split(',').map((p: string) => p.trim())
          : [],
        spaceRequired: moveMetadata?.spaceRequired || '',
        floorWork: moveMetadata?.floorWork || false,
        partnered: moveMetadata?.partnered || false,
        groupSize: moveMetadata?.groupSize || '',
        signature_moves: Array.isArray(moveMetadata?.signature_moves)
          ? moveMetadata?.signature_moves
          : typeof moveMetadata?.signature_moves === 'string' &&
            moveMetadata?.signature_moves
          ? moveMetadata.signature_moves.split(',').map((m: string) => m.trim())
          : [],
        techniques_required: Array.isArray(moveMetadata?.techniques_required)
          ? moveMetadata?.techniques_required
          : typeof moveMetadata?.techniques_required === 'string' &&
            moveMetadata?.techniques_required
          ? moveMetadata.techniques_required
              .split(',')
              .map((t: string) => t.trim())
          : [],
        props_needed: Array.isArray(moveMetadata?.props_needed)
          ? moveMetadata?.props_needed
          : typeof moveMetadata?.props_needed === 'string' &&
            moveMetadata?.props_needed
          ? moveMetadata.props_needed.split(',').map((p: string) => p.trim())
          : [],
        costume_requirements: moveMetadata?.costume_requirements || '',
        performanceContext: coerceEnumArray(
          moveMetadata?.performanceContext ?? moveMetadata?.performance_context,
          PERFORMANCE_CONTEXT_VALUES,
          10
        ),
        audience_type: moveMetadata?.audience_type || '',
      });
    }
  }, [computedHash, moveMetadata]);

  // Required fields for step validation – short debounce so Next enables quickly without hammering parent on every keystroke
  const MOVES_REQUIRED_FIELDS = ['moveName', 'tempo', 'danceStyle', 'difficulty'];
  const REQUIRED_DEBOUNCE_MS = 100;

  // Debounce timeouts for field changes
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const requiredFlushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildProcessedData = useCallback(
    (current: typeof editableData): MovesLayerMetadata => ({
      ...current,
      layerType: 'moves',
      // Required fields: use current value when set (including ''), so clearing the field is sent to parent
      moveName: (current.moveName !== undefined && current.moveName !== null) ? String(current.moveName) : (moveMetadata?.moveName ?? ''),
      tempo: (current.tempo !== undefined && current.tempo !== null) ? String(current.tempo) : (moveMetadata?.tempo ?? ''),
      danceStyle: (current.danceStyle !== undefined && current.danceStyle !== null) ? String(current.danceStyle) : (moveMetadata?.danceStyle ?? ''),
      difficulty: (current.difficulty !== undefined && current.difficulty !== null) ? String(current.difficulty) : (moveMetadata?.difficulty ?? ''),
      choreographerName: current.choreographerName || '',
      energy: coerceEnumValue(current.energy, INTENSITY_LEVEL_VALUES),
      complexity: current.complexity || '',
      danceClassification: moveMetadata?.danceClassification || {},
      energyTempo: moveMetadata?.energyTempo || '',
      movementPatterns: moveMetadata?.movementPatterns || [],
      culturalContext: moveMetadata?.culturalContext || '',
      performanceContext: coerceEnumArray(
        (current as any).performanceContext,
        PERFORMANCE_CONTEXT_VALUES,
        10
      ),
    }),
    [moveMetadata]
  );

  // Enhanced field change handler with debouncing (following LooksInformation pattern)
  const handleFieldChange = useCallback(
    (field: string, value: string | number | boolean | string[] | object) => {
      if (field.startsWith('bpmRange.')) {
        const bpmKey = field.split('.')[1] as 'min' | 'max';
        setEditableData(prev => ({
          ...prev,
          bpmRange: {
            ...prev.bpmRange,
            [bpmKey]: Number(value),
          },
        }));
      } else {
        setEditableData(prev => ({ ...prev, [field]: value }));
      }

      // Short debounce for required fields so validation updates quickly without hammering parent on every keystroke
      if (MOVES_REQUIRED_FIELDS.includes(field) && onMetadataChange) {
        if (requiredFlushTimeoutRef.current) {
          clearTimeout(requiredFlushTimeoutRef.current);
        }
        requiredFlushTimeoutRef.current = setTimeout(() => {
          requiredFlushTimeoutRef.current = null;
          setEditableData(current => {
            onMetadataChange(buildProcessedData(current));
            return current;
          });
        }, REQUIRED_DEBOUNCE_MS);
      }

      if (debounceTimeouts.current[field]) {
        clearTimeout(debounceTimeouts.current[field]);
      }

      debounceTimeouts.current[field] = setTimeout(() => {
        if (onMetadataChange) {
          setEditableData(current => {
            onMetadataChange(buildProcessedData(current));
            return current;
          });
        }
      }, 500);
    },
    [onMetadataChange, buildProcessedData]
  );

  // Cleanup timeouts on unmount AND flush pending changes
  // 🔧 FIX: Use empty dependency array to only run on unmount, use ref for latest data
  React.useEffect(() => {
    return () => {
      if (requiredFlushTimeoutRef.current) {
        clearTimeout(requiredFlushTimeoutRef.current);
        requiredFlushTimeoutRef.current = null;
      }
      // CRITICAL FIX: Flush all pending debounced changes before unmount
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });

      if (onMetadataChange) {
        const processedData = buildProcessedData(editableDataRef.current as any);
        onMetadataChange(processedData);
        console.log('💾 [MOVES METADATA] Flushing pending changes on unmount:', processedData);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoized dropdown options to prevent unnecessary re-renders
  const danceStyleOptions = useMemo(
    () => [
      'Ballet',
      'Contemporary',
      'Hip-Hop',
      'Jazz',
      'Tap',
      'Ballroom',
      'Latin',
      'Folk',
      'Modern',
      'Breakdancing',
      'Salsa',
      'Tango',
      'Waltz',
      'Swing',
      'Krumping',
      'Lyrical',
      'Commercial',
      'Street',
      'House',
      'Vogue',
    ],
    []
  );

  const difficultyOptions = useMemo(
    () => ['Beginner', 'Intermediate', 'Advanced', 'Professional'],
    []
  );

  const complexityOptions = useMemo(
    () => ['Simple', 'Moderate', 'Complex', 'Very Complex'],
    []
  );

  const energyOptions = useMemo(
    () => buildEnumOptions(INTENSITY_LEVEL_VALUES, intensityLevelLabels),
    []
  );

  const intensityOptions = useMemo(
    () => ['Light', 'Moderate', 'Intense', 'Very Intense'],
    []
  );

  const paceOptions = useMemo(() => ['Slow', 'Medium', 'Fast', 'Variable'], []);

  const bodyPartsOptions = useMemo(
    () => [
      'Arms',
      'Legs',
      'Torso',
      'Head',
      'Hands',
      'Feet',
      'Shoulders',
      'Hips',
      'Core',
    ],
    []
  );

  const spaceRequiredOptions = useMemo(
    () => ['Small', 'Medium', 'Large', 'Very Large'],
    []
  );

  const groupSizeOptions = useMemo(
    () => ['Solo', 'Duo', 'Small Group (3-5)', 'Large Group (6+)', 'Chorus'],
    []
  );

  const performanceContextOptions = useMemo(
    () => buildEnumOptions(PERFORMANCE_CONTEXT_VALUES, performanceContextLabels),
    []
  );

  const audienceTypeOptions = useMemo(
    () => [
      'General',
      'Youth',
      'Adult',
      'Professional',
      'Beginner',
      'Enthusiast',
    ],
    []
  );

  // Don't render if no metadata available
  if (
    !moveMetadata &&
    !asset.movesMetadata &&
    !asset.aiMetadata?.movesMetadata
  ) {
    return null;
  }

  return (
    <Box sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <DirectionsRunIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Move Metadata
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Core Move Information */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Core Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={danceStyleOptions}
              value={typeof editableData.danceStyle === 'string' ? editableData.danceStyle : Array.isArray(editableData.danceStyle) ? editableData.danceStyle.join(', ') : ''}
              inputValue={typeof editableData.danceStyle === 'string' ? editableData.danceStyle : Array.isArray(editableData.danceStyle) ? editableData.danceStyle.join(', ') : ''}
              onInputChange={(event, newValue) =>
                handleFieldChange('danceStyle', newValue || '')
              }
              renderInput={params => {
                const danceStyleStr = typeof editableData.danceStyle === 'string' ? editableData.danceStyle : Array.isArray(editableData.danceStyle) ? editableData.danceStyle.join(', ') : '';
                const isEmpty = !danceStyleStr || danceStyleStr.trim() === '';
                return (
                  <TextField
                    {...params}
                    label="Dance Style"
                    required
                    size="small"
                    inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                    error={isEmpty}
                    helperText={(isEmpty ? 'Dance style is required. ' : '') + `${API_STRING_MAX_LENGTH} characters max`}
                  />
                );
              }}
            />
          ) : (
            <Typography variant="body2">
              <strong>Dance Style:</strong>{' '}
              {typeof editableData.danceStyle === 'string' ? editableData.danceStyle : Array.isArray(editableData.danceStyle) ? editableData.danceStyle.join(', ') : editableData.danceStyle || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Move Name"
              value={editableData.moveName}
              onChange={e => handleFieldChange('moveName', e.target.value)}
              required
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
              error={!editableData.moveName || editableData.moveName.trim() === ''}
              helperText={(!editableData.moveName || editableData.moveName.trim() === '' ? 'Move name is required. ' : '') + `${API_STRING_MAX_LENGTH} characters max`}
            />
          ) : (
            <Typography variant="body2">
              <strong>Move Name:</strong>{' '}
              {editableData.moveName || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={paceOptions}
              value={typeof editableData.tempo === 'string' ? editableData.tempo : (typeof editableData.tempo === 'number' ? String(editableData.tempo) : '')}
              inputValue={typeof editableData.tempo === 'string' ? editableData.tempo : (typeof editableData.tempo === 'number' ? String(editableData.tempo) : '')}
              onInputChange={(event, newValue) =>
                handleFieldChange('tempo', newValue || '')
              }
              renderInput={params => {
                const tempoStr = typeof editableData.tempo === 'string' ? editableData.tempo : (typeof editableData.tempo === 'number' ? String(editableData.tempo) : '');
                return (
                <TextField 
                  {...params} 
                  label="Tempo" 
                  required
                  size="small"
                  error={!tempoStr || tempoStr.trim() === ''}
                  helperText={!tempoStr || tempoStr.trim() === '' ? 'Tempo is required' : ''}
                />
              );}}
            />
          ) : (
            <Typography variant="body2">
              <strong>Tempo:</strong>{' '}
              {(typeof editableData.tempo === 'string' || typeof editableData.tempo === 'number') ? String(editableData.tempo) : editableData.tempo || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Substyle"
              value={editableData.substyle}
              onChange={e => handleFieldChange('substyle', e.target.value)}
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
            />
          ) : (
            <Typography variant="body2">
              <strong>Substyle:</strong>{' '}
              {editableData.substyle || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Choreographer Name"
              value={editableData.choreographerName}
              onChange={e =>
                handleFieldChange('choreographerName', e.target.value)
              }
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
            />
          ) : (
            <Typography variant="body2">
              <strong>Choreographer:</strong>{' '}
              {editableData.choreographerName || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Movement Style"
              value={editableData.movementStyle}
              onChange={e => handleFieldChange('movementStyle', e.target.value)}
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
            />
          ) : (
            <Typography variant="body2">
              <strong>Movement Style:</strong>{' '}
              {editableData.movementStyle || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Difficulty & Performance */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Difficulty & Performance
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={difficultyOptions}
              value={typeof editableData.difficulty === 'string' ? editableData.difficulty : (typeof editableData.difficulty === 'number' ? String(editableData.difficulty) : '')}
              inputValue={typeof editableData.difficulty === 'string' ? editableData.difficulty : (typeof editableData.difficulty === 'number' ? String(editableData.difficulty) : '')}
              onInputChange={(event, newValue) =>
                handleFieldChange('difficulty', newValue || '')
              }
              renderInput={params => {
                const difficultyStr = typeof editableData.difficulty === 'string' ? editableData.difficulty : (typeof editableData.difficulty === 'number' ? String(editableData.difficulty) : '');
                return (
                <TextField 
                  {...params} 
                  label="Difficulty" 
                  required
                  size="small"
                  error={!difficultyStr || difficultyStr.trim() === ''}
                  helperText={!difficultyStr || difficultyStr.trim() === '' ? 'Difficulty is required' : ''}
                />
              );}}
            />
          ) : (
            <Typography variant="body2">
              <strong>Difficulty:</strong>{' '}
              {(typeof editableData.difficulty === 'string' || typeof editableData.difficulty === 'number') ? String(editableData.difficulty) : editableData.difficulty || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={complexityOptions}
              value={editableData.complexity}
              inputValue={editableData.complexity}
              onInputChange={(event, newValue) =>
                handleFieldChange('complexity', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Complexity" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Complexity:</strong>{' '}
              {editableData.complexity || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={difficultyOptions}
              value={editableData.skill_level_required}
              inputValue={editableData.skill_level_required}
              onInputChange={(event, newValue) =>
                handleFieldChange('skill_level_required', newValue || '')
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Skill Level Required"
                  size="small"
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Skill Level:</strong>{' '}
              {editableData.skill_level_required || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              options={energyOptions}
              value={energyOptions.find(option => option.value === editableData.energy) ?? null}
              onChange={(event, newValue) =>
                handleFieldChange('energy', newValue?.value || '')
              }
              renderInput={params => (
                <TextField {...params} label="Energy Level" size="small" />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Typography variant="body2">
              <strong>Energy:</strong> {editableData.energy ? intensityLevelLabels[editableData.energy as IntensityLevel] : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={intensityOptions}
              value={editableData.intensity}
              inputValue={editableData.intensity}
              onInputChange={(event, newValue) =>
                handleFieldChange('intensity', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Intensity" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Intensity:</strong>{' '}
              {editableData.intensity || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={paceOptions}
              value={editableData.pace}
              inputValue={editableData.pace}
              onInputChange={(event, newValue) =>
                handleFieldChange('pace', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Pace" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Pace:</strong> {editableData.pace || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* BPM Range */}
        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Min BPM"
                type="number"
                value={editableData.bpmRange.min}
                onChange={e =>
                  handleFieldChange('bpmRange.min', e.target.value)
                }
                size="small"
                sx={{ flex: 1 }}
              />
              <Typography sx={{ px: 1 }}>-</Typography>
              <TextField
                label="Max BPM"
                type="number"
                value={editableData.bpmRange.max}
                onChange={e =>
                  handleFieldChange('bpmRange.max', e.target.value)
                }
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>
          ) : (
            <Typography variant="body2">
              <strong>BPM Range:</strong>{' '}
              {editableData.bpmRange.min > 0 || editableData.bpmRange.max > 0
                ? `${editableData.bpmRange.min}-${editableData.bpmRange.max}`
                : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Physical Requirements */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Physical Requirements
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={bodyPartsOptions}
              value={(editableData.bodyParts || []).slice(0, MOVES_METADATA.bodyParts.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('bodyParts', (newValue ?? []).slice(0, MOVES_METADATA.bodyParts.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Body Parts"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${MOVES_METADATA.bodyParts.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Body Parts:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.bodyParts || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.bodyParts?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={spaceRequiredOptions}
              value={editableData.spaceRequired}
              inputValue={editableData.spaceRequired}
              onInputChange={(event, newValue) =>
                handleFieldChange('spaceRequired', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Space Required" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Space Required:</strong>{' '}
              {editableData.spaceRequired || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={groupSizeOptions}
              value={editableData.groupSize}
              inputValue={editableData.groupSize}
              onInputChange={(event, newValue) =>
                handleFieldChange('groupSize', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Group Size" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Group Size:</strong>{' '}
              {editableData.groupSize || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <FormGroup>
              <Typography variant="subtitle2" gutterBottom>
                Performance Style
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={editableData.floorWork}
                    onChange={e =>
                      handleFieldChange('floorWork', e.target.checked)
                    }
                  />
                }
                label="Floor Work"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editableData.partnered}
                    onChange={e =>
                      handleFieldChange('partnered', e.target.checked)
                    }
                  />
                }
                label="Partnered"
              />
            </FormGroup>
          ) : (
            <Box>
              <Typography variant="body2">
                <strong>Floor Work:</strong>{' '}
                {editableData.floorWork ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2">
                <strong>Partnered:</strong>{' '}
                {editableData.partnered ? 'Yes' : 'No'}
              </Typography>
            </Box>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Technical Details */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Technical Details
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={[
                'Balance',
                'Flexibility',
                'Strength',
                'Coordination',
                'Rhythm',
                'Agility',
              ]}
              value={(editableData.techniques_required || []).slice(0, MOVES_METADATA.techniquesRequired.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('techniques_required', (newValue ?? []).slice(0, MOVES_METADATA.techniquesRequired.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Techniques Required"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${MOVES_METADATA.techniquesRequired.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Techniques Required:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.techniques_required || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.techniques_required?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={[
                'Jumps',
                'Turns',
                'Leaps',
                'Lifts',
                'Spins',
                'Extensions',
              ]}
              value={(editableData.signature_moves || []).slice(0, MOVES_METADATA.signatureMoves.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('signature_moves', (newValue ?? []).slice(0, MOVES_METADATA.signatureMoves.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Signature Moves"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${MOVES_METADATA.signatureMoves.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Signature Moves:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.signature_moves || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.signature_moves?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={[
                'Scarves',
                'Ribbons',
                'Chairs',
                'Canes',
                'Fans',
                'None',
              ]}
              value={(editableData.props_needed || []).slice(0, MOVES_METADATA.propsNeeded.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('props_needed', (newValue ?? []).slice(0, MOVES_METADATA.propsNeeded.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Props Needed"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${MOVES_METADATA.propsNeeded.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Props Needed:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.props_needed || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.props_needed?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Costume Requirements"
              value={editableData.costume_requirements}
              onChange={e =>
                handleFieldChange('costume_requirements', e.target.value)
              }
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
            />
          ) : (
            <Typography variant="body2">
              <strong>Costume Requirements:</strong>{' '}
              {editableData.costume_requirements || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              options={performanceContextOptions}
              value={performanceContextOptions.filter(option =>
                (editableData.performanceContext || []).includes(option.value)
              )}
              onChange={(event, newValue) =>
                handleFieldChange(
                  'performanceContext',
                  newValue.map(option => option.value)
                )
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Performance Context"
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Performance Context:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.performanceContext || []).map((context: PerformanceContext, index: number) => (
                  <Chip key={index} label={performanceContextLabels[context]} size="small" variant="outlined" />
                ))}
                {(!editableData.performanceContext || editableData.performanceContext.length === 0) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={audienceTypeOptions}
              value={editableData.audience_type}
              inputValue={editableData.audience_type}
              onInputChange={(event, newValue) =>
                handleFieldChange('audience_type', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Audience Type" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Audience Type:</strong>{' '}
              {editableData.audience_type || 'Not specified'}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MovesMetadataEditor;
